/**
 * Module summary:
 * This page shows the user's assessment history. It combines cached local data
 * with a backend sync, then lets the user search, filter, sort, continue, and
 * delete assessments from one workspace.
 */

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import AppShell from '../layout/AppShell';
import {
  listAssessmentsByUser,
  deleteAssessment,
  syncAssessmentsByUser,
  createFollowUpAssessment,
  type AssessmentRecord,
} from '../../services/data';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import './pages.css';

export const PastAssessments: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'in-progress'>('all');
  const [buildingFilter, setBuildingFilter] = useState('all');
  const [sortBy, setSortBy] = useState<'updatedDesc' | 'updatedAsc' | 'scoreDesc' | 'scoreAsc' | 'nameAsc' | 'nameDesc'>('updatedDesc');
  const [rows, setRows] = useState<AssessmentRecord[]>([]);
  const [followUpSource, setFollowUpSource] = useState<AssessmentRecord | null>(null);
  const [followUpDraft, setFollowUpDraft] = useState({ name: '', address: '', buildingType: '' });

  useEffect(() => {
    if (!user?.id) {
      // Clear the page when no user is active so old report rows do not remain visible.
      setRows([]);
      return;
    }
    void refreshKey;
    // Show local rows immediately, then replace them with the latest synced rows when the async refresh finishes.
    setRows(listAssessmentsByUser(user.id));
    void syncAssessmentsByUser(user.id).then(setRows);
  }, [user?.id, refreshKey]);

  const buildingTypes = useMemo(() => {
    const values = new Set<string>();
    rows.forEach((row) => {
      // Gather distinct building types so the filter menu only shows values that actually exist in the data set.
      if (row.buildingType && row.buildingType.trim()) values.add(row.buildingType.trim());
    });
    return ['all', ...Array.from(values).sort((a, b) => a.localeCompare(b))];
  }, [rows]);

  const filteredRows = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    const filtered = rows.filter((row) => {
      const rowStatus = row.status === 'completed' ? 'completed' : 'in-progress';
      // Apply status, building, and free-text filters together so the user sees one coherent result set.
      const statusMatch = statusFilter === 'all' || rowStatus === statusFilter;
      const buildingMatch = buildingFilter === 'all' || (row.buildingType || '').trim() === buildingFilter;
      const searchMatch = !needle || [row.name, row.address, row.buildingType, row.id]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));
      return statusMatch && buildingMatch && searchMatch;
    });

    return filtered.sort((a, b) => {
      // Sort the already filtered rows based on the active UI sort choice.
      switch (sortBy) {
        case 'updatedAsc':
          return a.updatedAt.localeCompare(b.updatedAt);
        case 'updatedDesc':
          return b.updatedAt.localeCompare(a.updatedAt);
        case 'scoreAsc':
          return (a.score ?? 0) - (b.score ?? 0);
        case 'scoreDesc':
          return (b.score ?? 0) - (a.score ?? 0);
        case 'nameDesc':
          return b.name.localeCompare(a.name);
        case 'nameAsc':
        default:
          return a.name.localeCompare(b.name);
      }
    });
  }, [rows, searchText, statusFilter, buildingFilter, sortBy]);

  const completed = filteredRows.filter((row) => row.status === 'completed');
  const inProgress = filteredRows.filter((row) => row.status !== 'completed');
  const visibleCompleted = completed.slice(0, 5);
  const visibleInProgress = inProgress.slice(0, 5);

  const handleDelete = useCallback(async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      // Wait for the local and remote delete to finish before reloading the reports list.
      await deleteAssessment(id);
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Could not delete the assessment.');
    }
  }, []);

  const handleCreateFollowUp = useCallback(async (row: AssessmentRecord) => {
    setFollowUpSource(row);
    setFollowUpDraft({
      name: row.name,
      address: row.address || '',
      buildingType: row.buildingType || '',
    });
  }, []);

  const handleConfirmFollowUp = useCallback(async () => {
    if (!followUpSource) return;
    try {
      const next = await createFollowUpAssessment({
        ...followUpSource,
        userId: user?.id || followUpSource.userId,
      }, {
        name: followUpDraft.name,
        address: followUpDraft.address,
        addressParts: {
          ...followUpSource.addressParts,
        },
        buildingType: followUpDraft.buildingType,
      });
      setFollowUpSource(null);
      setRefreshKey((prev) => prev + 1);
      navigate(`/assessment?id=${encodeURIComponent(next.id)}`);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Could not create the follow-up assessment.');
    }
  }, [followUpDraft.address, followUpDraft.buildingType, followUpDraft.name, followUpSource, navigate, user?.id]);

  return (
    <AppShell title="Reports" isDashboard>
      <div className="content-page reports-page">
        <section className="content-page__hero content-page__hero--dark">
          <div className="reports-hero">
            <div className="content-page__hero-copy">
              <div className="reports-hero__brand">
                <img src="/Transparent-Logo.png" alt="Macha Group" />
                <span>Macha Group Reports</span>
              </div>
              <p className="content-page__eyebrow">Reports</p>
              <h1 className="content-page__title">Assessment reports and active work.</h1>
              <p className="content-page__subtitle">
                Track in-progress inspections, review completed reports, and jump back into active assessments.
              </p>
            </div>
            <button type="button" className="dashboard-btn dashboard-btn--primary reports-hero__action" onClick={() => navigate('/create-assessment')}>
              New Assessment
            </button>
          </div>
        </section>

        <section className="reports-summary">
          <article className="dashboard-kpi">
            <p className="dashboard-kpi__label">Total</p>
            <strong className="dashboard-kpi__value">{filteredRows.length}</strong>
            <p className="dashboard-kpi__helper">
              {filteredRows.length === rows.length
                ? 'Assessments in your workspace'
                : `Filtered from ${rows.length} total`}
            </p>
          </article>
          <article className="dashboard-kpi">
            <p className="dashboard-kpi__label">Completed</p>
            <strong className="dashboard-kpi__value">{completed.length}</strong>
            <p className="dashboard-kpi__helper">Final reports ready for review</p>
          </article>
          <article className="dashboard-kpi">
            <p className="dashboard-kpi__label">In Progress</p>
            <strong className="dashboard-kpi__value">{inProgress.length}</strong>
            <p className="dashboard-kpi__helper">Assessments still in progress</p>
          </article>
        </section>

        {rows.length > 0 && (
          <section className="content-card reports-filters">
            <div className="reports-filters__grid">
              <label className="reports-filters__field reports-filters__field--search">
                <span>Search</span>
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search by name, address, type, or ID"
                />
              </label>
              <label className="reports-filters__field">
                <span>Status</span>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}>
                  <option value="all">All</option>
                  <option value="completed">Completed</option>
                  <option value="in-progress">In Progress</option>
                </select>
              </label>
              <label className="reports-filters__field">
                <span>Building Type</span>
                <select value={buildingFilter} onChange={(e) => setBuildingFilter(e.target.value)}>
                  {buildingTypes.map((type) => (
                    <option key={type} value={type}>
                      {type === 'all' ? 'All Types' : type}
                    </option>
                  ))}
                </select>
              </label>
              <label className="reports-filters__field">
                <span>Sort</span>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)}>
                  <option value="updatedDesc">Newest First</option>
                  <option value="updatedAsc">Oldest First</option>
                  <option value="scoreDesc">Highest Score</option>
                  <option value="scoreAsc">Lowest Score</option>
                  <option value="nameAsc">Name A-Z</option>
                  <option value="nameDesc">Name Z-A</option>
                </select>
              </label>
            </div>
          </section>
        )}

        {rows.length === 0 ? (
          <section className="content-card reports-empty">
            <p className="content-card__eyebrow">No Reports Yet</p>
            <h2 className="content-card__title">Create your first assessment.</h2>
            <p className="content-card__copy">Start a new walkthrough to generate findings, progress tracking, and reports.</p>
            <button type="button" className="dashboard-btn dashboard-btn--solid" onClick={() => navigate('/create-assessment')}>
              Create Assessment
            </button>
          </section>
        ) : (
          <>
            {inProgress.length > 0 && (
              <section className="content-card reports-group">
                <div className="content-card__header">
                  <div>
                    <p className="content-card__eyebrow">In Progress</p>
                    <h2 className="content-card__title">{inProgress.length} active assessments</h2>
                  </div>
                </div>

                <div className="reports-list">
                  {visibleInProgress.map((row) => (
                    <article key={row.id} className="report-row">
                      <div className="report-row__main">
                        <h3 className="report-row__title">{row.name}</h3>
                        <p className="report-row__meta">
                          {row.address || 'Location pending'} - {row.buildingType || 'Facility review'}
                        </p>
                      </div>
                      <div className="report-row__actions">
                        <span className="dashboard-status dashboard-status--active">In Progress</span>
                        <button type="button" className="report-row__button" onClick={() => navigate(`/assessment?id=${encodeURIComponent(row.id)}`)}>
                          Continue
                        </button>
                        <button type="button" className="report-row__button report-row__button--danger" onClick={() => void handleDelete(row.id, row.name)}>
                          Delete
                        </button>
                      </div>
                    </article>
                  ))}
                  {inProgress.length > 5 && (
                    <p className="content-card__copy">Showing 5 of {inProgress.length} in-progress assessments.</p>
                  )}
                </div>
              </section>
            )}

            {completed.length > 0 && (
              <section className="content-card reports-group">
                <div className="content-card__header">
                  <div>
                    <p className="content-card__eyebrow">Completed</p>
                    <h2 className="content-card__title">{completed.length} completed reports</h2>
                  </div>
                </div>

                <div className="reports-list">
                  {visibleCompleted.map((row) => (
                    <article key={row.id} className="report-row">
                      <div className="report-row__main">
                        <h3 className="report-row__title">{row.name}</h3>
                        <p className="report-row__meta">
                          {row.address || 'Location pending'} - {new Date(row.updatedAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </p>
                      </div>
                      <div className="report-row__actions">
                        <div className="report-row__score">
                          <span>Score</span>
                          <strong>{row.score ?? 0}%</strong>
                        </div>
                        <button type="button" className="report-row__button" onClick={() => void handleCreateFollowUp(row)}>
                          Follow-Up
                        </button>
                        <button type="button" className="report-row__button report-row__button--danger" onClick={() => handleDelete(row.id, row.name)}>
                          Delete
                        </button>
                        <button type="button" className="dashboard-btn dashboard-btn--solid report-row__cta" onClick={() => navigate(`/report/${row.id}`)}>
                          View Report
                        </button>
                      </div>
                    </article>
                  ))}
                  {completed.length > 5 && (
                    <p className="content-card__copy">Showing 5 of {completed.length} completed reports.</p>
                  )}
                </div>
              </section>
            )}

            {filteredRows.length === 0 && rows.length > 0 && (
              <section className="content-card reports-empty">
                <p className="content-card__eyebrow">No Matches</p>
                <h2 className="content-card__title">No reports match your filters.</h2>
                <p className="content-card__copy">Adjust search, status, or sort settings to see more reports.</p>
              </section>
            )}
          </>
        )}
      </div>
      {followUpSource && (
        <div className="modal-overlay" role="presentation">
          <div className="modal-card">
            <h2 className="content-card__title">Review Follow-Up</h2>
            <p className="content-card__copy">Keep the same report name or edit the details before starting the follow-up assessment.</p>
            <label className="contact-field">
              <span>Name</span>
              <input value={followUpDraft.name} onChange={(e) => setFollowUpDraft((prev) => ({ ...prev, name: e.target.value }))} />
            </label>
            <label className="contact-field">
              <span>Address</span>
              <input value={followUpDraft.address} onChange={(e) => setFollowUpDraft((prev) => ({ ...prev, address: e.target.value }))} />
            </label>
            <label className="contact-field">
              <span>Building Type</span>
              <input value={followUpDraft.buildingType} onChange={(e) => setFollowUpDraft((prev) => ({ ...prev, buildingType: e.target.value }))} />
            </label>
            <div className="profile-form__actions">
              <button type="button" className="report-row__button" onClick={() => setFollowUpSource(null)}>
                Cancel
              </button>
              <button type="button" className="dashboard-btn dashboard-btn--solid" onClick={() => void handleConfirmFollowUp()}>
                Start Follow-Up
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
};

export default PastAssessments;


